import React, { useEffect, useMemo, useState } from "react";
import styles from "./BonusTab.module.css";
import { useDispatch, useSelector } from "react-redux";
import useAuth from "../../hooks/useAuth";
import {
  getAllRankingHistoryByID,
  getUsersByID,
  getPlayerBalance,
} from "../../store/slices/user";
import apiClient from "../../store/services/apiClient";

export default function BonusTab() {
  const dispatch = useDispatch();
  const { user } = useAuth();
  const {
    allRanking = [],
    allRankingHistoryByID = [],
    singleUsers = [],
    colorSettings = [],
  } = useSelector((s) => s.user || {});

  const [openReceive, setOpenReceive] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [message, setMessage] = useState("");

  const apiBase = process.env.REACT_APP_API_BASE_URL || "";

  useEffect(() => {
    if (user?.id) {
      dispatch(getAllRankingHistoryByID(user.id));
    }
  }, [dispatch, user?.id]);

  const currentUser = singleUsers?.[0] || {};
  const currentClass = Number(currentUser?.rankClass ?? 0);
  const currentRank = allRanking?.[currentClass] || {};
  const nextRank = allRanking?.[currentClass + 1] || null;
  const depositAmount = Number(currentUser?.deposit_notpro_amount ?? 0);
  const target = Number(nextRank?.rankReceiveConditionAmount ?? 0);
  const progress =
    target > 0
      ? Math.min(100, Math.round((depositAmount / target) * 100))
      : 100;

  const filteredRankingHistory = useMemo(
    () =>
      (allRankingHistoryByID || []).filter(
        (i) => i?.receiveStatus === "RECEIVING",
      ),
    [allRankingHistoryByID],
  );

  const canCheckBonus =
    currentUser?.PromotionStatus === "INACTIVE" &&
    (currentUser?.withdrawConditionType === null ||
      currentUser?.withdrawConditionType === undefined);

  const handleCheckBonus = async () => {
    if (!user?.id) return;
    setMessage("");
    try {
      await apiClient.get(`/ranking/checkrank/${user.id}`);
      await dispatch(getAllRankingHistoryByID(user.id));
    } catch (e) {
      // ignore
    }
    setOpenReceive(true);
  };

  const handleReceiveBonus = async (rowId) => {
    if (!user?.id) return;
    setLoadingId(rowId);
    setMessage("");
    try {
      const body = { data: rowId };
      const r = await apiClient.post(`/ranking/receiverank/${user.id}`, body);
      const msg = r?.data?.message || "";
      const ok =
        msg === "รับโบนัสเลื่อนแรงค์ with Withdrawal Condition" ||
        msg === "รับโบนัสเลื่อนแรงค์ Successfully";
      if (ok) {
        setMessage("รับโบนัสเลื่อนระดับเรียบร้อยแล้ว");
        setTimeout(async () => {
          await dispatch(getUsersByID(user));
          await dispatch(getPlayerBalance(user));
          setLoadingId(null);
          setOpenReceive(false);
        }, 1200);
      } else {
        setMessage("กรุณาเคลียร์เทิร์นก่อนรับโบนัสเลื่อนระดับ");
        setTimeout(() => setLoadingId(null), 800);
      }
    } catch (e) {
      setMessage("กรุณาเคลียร์เทิร์นก่อนรับโบนัสเลื่อนระดับ");
      setTimeout(() => setLoadingId(null), 800);
    }
  };

  return (
    <div className={styles.wrapper}>
      {/* ── Hero ── */}
      <div className={styles.hero}>
        <div className={styles.heroGlow} />
        <div className={styles.heroAvatar}>
          {currentRank?.rankImageURL ? (
            <img
              className={styles.heroImg}
              src={`${apiBase}/uploads${currentRank.rankImageURL}`}
              alt={currentRank?.rankName || "rank"}
              loading="lazy"
            />
          ) : (
            <span className={styles.heroEmoji}>🏆</span>
          )}
        </div>
        <h2 className={styles.heroTitle}>ระบบจัดอันดับสมาชิก</h2>
        <p className={styles.heroDesc}>
          สะสมยอดฝากเพื่อปลดล็อกสิทธิพิเศษทุกระดับ
        </p>
      </div>

      {/* ── Current Rank Strip ── */}
      <div className={styles.rankStrip}>
        <div className={styles.stripLeft}>
          <div className={styles.stripBadge}>
            {currentRank?.rankImageURL ? (
              <img
                className={styles.stripImg}
                src={`${apiBase}/uploads${currentRank.rankImageURL}`}
                alt={currentRank?.rankName || "rank"}
                loading="lazy"
              />
            ) : (
              <span>🏅</span>
            )}
          </div>
          <div className={styles.stripMeta}>
            <span className={styles.stripRankName}>
              {currentRank?.rankName || "สมาชิก"}
            </span>
            <span className={styles.stripLabel}>ระดับปัจจุบันของคุณ</span>
          </div>
        </div>
        {nextRank && (
          <div className={styles.stripRight}>
            <span className={styles.stripTarget}>
              เป้าหมาย {target.toLocaleString()} ฿
            </span>
          </div>
        )}
      </div>

      {/* ── Progress ── */}
      {nextRank && (
        <div className={styles.progressSection}>
          <div className={styles.progressInfo}>
            <span className={styles.progressLabel}>ยอดฝากสะสม</span>
            <span className={styles.progressValue}>
              {depositAmount.toLocaleString()} / {target.toLocaleString()} บาท
            </span>
          </div>
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${progress}%` }}
            />
          </div>
          <div className={styles.progressFooter}>
            <span className={styles.progressPct}>{progress}%</span>
            <span className={styles.progressHint}>
              อีก {Math.max(0, target - depositAmount).toLocaleString()} บาท
              เพื่อเลื่อนขั้น
            </span>
          </div>
        </div>
      )}

      {/* ── Action ── */}
      <div className={styles.actionArea}>
        {canCheckBonus ? (
          <button className={styles.actionBtn} onClick={handleCheckBonus}>
            🎁 ตรวจสอบโบนัสเลื่อนระดับ
          </button>
        ) : (
          <div className={styles.warningBox}>
            <span className={styles.warningEmoji}>⚠️</span>
            <div className={styles.warningContent}>
              <span className={styles.warningTitle}>
                ยังไม่สามารถรับโบนัสได้
              </span>
              <span className={styles.warningDesc}>
                กรุณาเคลียร์ยอดเทิร์นให้ครบก่อนรับโบนัสเลื่อนระดับ
              </span>
            </div>
          </div>
        )}
      </div>

      {/* ── Roadmap ── */}
      <div className={styles.sectionTitle}>🗺️ เส้นทางอันดับทั้งหมด</div>
      <div className={styles.roadmap}>
        {allRanking?.map((row, index) => {
          const isCurrent = currentClass === index;
          const isDone = currentClass > index;
          return (
            <div
              key={index}
              className={`${styles.roadItem} ${isCurrent ? styles.roadCurrent : ""} ${isDone ? styles.roadDone : ""}`}
            >
              <div className={styles.roadLine}>
                <div
                  className={`${styles.roadDot} ${isDone ? styles.roadDotDone : ""} ${isCurrent ? styles.roadDotCurrent : ""}`}
                />
                {index < allRanking.length - 1 && (
                  <div
                    className={`${styles.roadConnector} ${isDone ? styles.roadConnectorDone : ""}`}
                  />
                )}
              </div>
              <div className={styles.roadCard}>
                <div className={styles.roadCardLeft}>
                  {row?.rankImageURL ? (
                    <img
                      className={styles.roadImg}
                      src={`${apiBase}/uploads${row.rankImageURL}`}
                      alt={row?.rankName || "rank"}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.roadImgPlaceholder}>🎖️</div>
                  )}
                </div>
                <div className={styles.roadCardMid}>
                  <span className={styles.roadName}>
                    {row?.rankName || "-"}
                  </span>
                  <span className={styles.roadCondition}>
                    ยอดฝาก{" "}
                    {(row?.rankReceiveConditionAmount ?? 0).toLocaleString()}{" "}
                    บาท
                  </span>
                </div>
                <div className={styles.roadCardRight}>
                  {isDone ? (
                    <span className={styles.tagDone}>✓ ผ่านแล้ว</span>
                  ) : isCurrent ? (
                    <span className={styles.tagNow}>กำลังใช้งาน</span>
                  ) : (
                    <span className={styles.tagReward}>
                      +{(row?.rankBonusAmount ?? 0).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Receive Modal ── */}
      {openReceive && (
        <div
          className={styles.modalOverlay}
          onClick={() => setOpenReceive(false)}
        >
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <span className={styles.modalTitle}>🎁 รับโบนัสเลื่อนระดับ</span>
              <button
                className={styles.closeBtn}
                onClick={() => setOpenReceive(false)}
              >
                ✕
              </button>
            </div>
            <div className={styles.modalBody}>
              {message && <div className={styles.toast}>{message}</div>}

              {filteredRankingHistory.length === 0 && (
                <div className={styles.emptyState}>
                  <span className={styles.emptyEmoji}>📭</span>
                  <span className={styles.emptyText}>
                    ยังไม่มีโบนัสที่สามารถรับได้
                  </span>
                </div>
              )}

              {filteredRankingHistory.map((row, index) => {
                const before = allRanking?.[Number(row?.rankclassBefore) || 0];
                const after = allRanking?.[Number(row?.rankclassAfter) || 0];
                return (
                  <div key={index} className={styles.receiveCard}>
                    <div className={styles.receiveFlow}>
                      <div className={styles.receiveRank}>
                        {before?.rankImageURL ? (
                          <img
                            className={styles.receiveImg}
                            src={`${apiBase}/uploads${before.rankImageURL}`}
                            alt={before?.rankName || "before"}
                          />
                        ) : (
                          <span className={styles.receiveImgFallback}>🏅</span>
                        )}
                        <span className={styles.receiveRankName}>
                          {before?.rankName || "—"}
                        </span>
                      </div>
                      <span className={styles.receiveArrow}>→</span>
                      <div className={styles.receiveRank}>
                        {after?.rankImageURL ? (
                          <img
                            className={styles.receiveImg}
                            src={`${apiBase}/uploads${after.rankImageURL}`}
                            alt={after?.rankName || "after"}
                          />
                        ) : (
                          <span className={styles.receiveImgFallback}>🏆</span>
                        )}
                        <span className={styles.receiveRankName}>
                          {after?.rankName || "—"}
                        </span>
                      </div>
                    </div>
                    {row?.withdrawCondition && (
                      <div className={styles.receiveNote}>
                        {row.withdrawCondition}
                      </div>
                    )}
                    {loadingId === row?.id && (
                      <div className={styles.loadingBar} />
                    )}
                    <button
                      className={styles.receiveBtn}
                      disabled={!!loadingId}
                      onClick={() => handleReceiveBonus(row?.id)}
                    >
                      {loadingId === row?.id ? "กำลังดำเนินการ..." : "รับโบนัส"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
